import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LayoutTemplate, Pencil, SquarePen, Trash2 } from 'lucide-react'
import {
  listTemplates,
  renameTemplate,
  deleteTemplate,
  startEditSession,
  type ReportTemplate,
} from '../features/reports/reportTemplatesApi'

export default function ReportTemplates() {
  const navigate = useNavigate()
  const [templates, setTemplates] = useState<ReportTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [editing, setEditing] = useState<ReportTemplate | null>(null)
  const [projects, setProjects] = useState<{ id: number; name: string }[]>([])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    listTemplates()
      .then((list) => {
        if (!cancelled) setTemplates(list)
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Erreur de chargement')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    fetch('/api/clients', { credentials: 'include' })
      .then((r) => r.json())
      .then((body) => {
        if (!cancelled && body?.data) setProjects(body.data)
      })
      .catch(() => {
        // silencieux : la modale affichera une liste vide
      })
    return () => {
      cancelled = true
    }
  }, [])

  async function startEdit(projectId: number) {
    if (!editing) return
    const { reportId } = await startEditSession(editing.id, projectId)
    navigate(`/p/${projectId}/reports/${reportId}`)
  }

  async function handleRename(t: ReportTemplate) {
    const name = window.prompt('Nom du template', t.name)
    if (!name || !name.trim() || name.trim() === t.name) return
    const trimmed = name.trim()
    setBusyId(t.id)
    try {
      await renameTemplate(t.id, trimmed)
      setTemplates((prev) => prev.map((x) => (x.id === t.id ? { ...x, name: trimmed } : x)))
    } catch {
      // silencieux : pas de crash de la page
    } finally {
      setBusyId(null)
    }
  }

  async function handleDelete(t: ReportTemplate) {
    if (!window.confirm('Supprimer ce template ?')) return
    setBusyId(t.id)
    try {
      await deleteTemplate(t.id)
      setTemplates((prev) => prev.filter((x) => x.id !== t.id))
    } catch {
      // silencieux
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="text-[#1B2559]">
      {/* Page title */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-[#1B2559]">Mes templates</h2>
          <p className="text-[#8B8FA7] text-sm mt-0.5">Réutilisez vos rapports comme modèles</p>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-orion-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {!loading && error && (
        <div className="bg-score-critical/10 border border-score-critical/20 rounded-xl p-4 text-score-critical text-sm">
          Erreur : {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {templates.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {templates.map((t) => (
                <div
                  key={t.id}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-[#E2E4ED] bg-white transition-all hover:border-[#6366F1] hover:shadow-[0_4px_16px_rgba(99,102,241,0.08)]"
                >
                  {/* En-tête visuel : tuile d'icône sur fond doux */}
                  <div className="flex h-24 items-center justify-center border-b border-[#E2E4ED] bg-gradient-to-br from-[#F4F5FA] to-[#ECEDFB]">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white shadow-sm">
                      <LayoutTemplate className="h-5 w-5 text-[#6366F1]" strokeWidth={1.5} />
                    </div>
                  </div>

                  {/* Corps */}
                  <div className="flex-1 p-4">
                    <p className="truncate font-semibold text-[#1B2559]" title={t.name}>
                      {t.name}
                    </p>
                    <p className="mt-0.5 line-clamp-2 text-xs text-[#8B8FA7]">
                      {t.description || 'Modèle de rapport réutilisable'}
                    </p>
                    <p className="mt-3 text-[11px] text-[#B0B4C5]">
                      Modifié le{' '}
                      {new Date(t.updatedAt || t.createdAt).toLocaleDateString('fr-FR')}
                    </p>
                  </div>

                  {/* Actions — toujours visibles */}
                  <div className="flex items-center justify-end gap-1 border-t border-[#F0F1F6] px-3 py-2">
                    <button
                      onClick={() => setEditing(t)}
                      disabled={busyId === t.id}
                      aria-label="Modifier le template"
                      className="rounded-lg p-1.5 text-[#555A73] transition-colors hover:bg-[#6366F1]/10 hover:text-[#6366F1] disabled:opacity-50"
                    >
                      <SquarePen className="h-4 w-4" strokeWidth={1.25} />
                    </button>
                    <button
                      onClick={() => handleRename(t)}
                      disabled={busyId === t.id}
                      aria-label="Renommer le template"
                      className="rounded-lg p-1.5 text-[#555A73] transition-colors hover:bg-[#6366F1]/10 hover:text-[#6366F1] disabled:opacity-50"
                    >
                      <Pencil className="h-4 w-4" strokeWidth={1.25} />
                    </button>
                    <button
                      onClick={() => handleDelete(t)}
                      disabled={busyId === t.id}
                      aria-label="Supprimer le template"
                      className="rounded-lg p-1.5 text-[#555A73] transition-colors hover:bg-score-critical/10 hover:text-score-critical disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={1.25} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 rounded-2xl bg-white border border-[#E2E4ED] flex items-center justify-center mb-4">
                <LayoutTemplate className="w-7 h-7 text-[#555A73]" strokeWidth={1.25} />
              </div>
              <p className="text-[#1B2559] font-semibold mb-1">Aucun template</p>
              <p className="text-[#8B8FA7] text-sm">
                Enregistrez un rapport comme template depuis le builder.
              </p>
            </div>
          )}
        </>
      )}

      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#1B2559]/40 p-4"
          onClick={() => setEditing(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="w-full max-w-md rounded-2xl border border-[#E2E4ED] bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-[#1B2559]">
              Modifier « {editing.name} »
            </h3>
            <p className="mt-0.5 text-sm text-[#8B8FA7]">Choisis un projet d'aperçu</p>

            <div className="mt-4 flex max-h-72 flex-col gap-1 overflow-y-auto">
              {projects.length > 0 ? (
                projects.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => startEdit(p.id)}
                    className="truncate rounded-xl border border-[#E2E4ED] px-4 py-2.5 text-left text-sm font-medium text-[#1B2559] transition-colors hover:border-[#6366F1] hover:bg-[#6366F1]/5"
                  >
                    {p.name}
                  </button>
                ))
              ) : (
                <p className="py-4 text-center text-sm text-[#8B8FA7]">Aucun projet disponible</p>
              )}
            </div>

            <div className="mt-5 flex justify-end">
              <button
                onClick={() => setEditing(null)}
                className="rounded-xl px-4 py-2 text-sm font-medium text-[#555A73] transition-colors hover:bg-[#F4F5FA]"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
